import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
} from "react";
import {
  useKnowledgeItems,
  KnowledgeItemType,
} from "@/hooks/knowledge/useKnowledgeItems";
import { useCombinedAgentList } from "@/hooks/agent/useCombinedAgentList";

// Types for knowledge items (matching the existing hook)
export interface KnowledgeItem {
  id: string;
  title: string;
  type: KnowledgeItemType;
  content?: string;
  url?: string;
  file_path?: string;
  agent_id?: string;
  voice_agent_id?: string;
  created_at: string;
  updated_at: string;
}

export interface Agent {
  id: string;
  agent_name?: string;
  title: string;
  type: "chat" | "voice";
}

// Filter types
type FilterType = "all" | "links" | "files" | "snippets";

// Cache TTL - 30 minutes
const CACHE_TTL = 30 * 60 * 1000;

// Types for the context
interface KnowledgeState {
  items: KnowledgeItem[];
  agents: Agent[];
  isLoading: boolean;
  agentsLoading: boolean;
  error: Error | null;
  isInitialized: boolean;
  lastFetchTime: number | null;

  // Filter states - persisted across navigation
  orgFilter: FilterType;
  agentFilter: FilterType;
  openSections: string[];

  // Cache for filtered results
  filteredResultsCache: Map<
    string,
    { orgItems: KnowledgeItem[]; agentItems: KnowledgeItem[] }
  >;
}

type KnowledgeAction =
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_AGENTS_LOADING"; payload: boolean }
  | { type: "SET_ITEMS"; payload: KnowledgeItem[] }
  | { type: "SET_AGENTS"; payload: Agent[] }
  | { type: "SET_ERROR"; payload: Error | null }
  | { type: "SET_INITIALIZED"; payload: boolean }
  | { type: "SET_LAST_FETCH_TIME"; payload: number }
  | { type: "SET_ORG_FILTER"; payload: FilterType }
  | { type: "SET_AGENT_FILTER"; payload: FilterType }
  | { type: "SET_OPEN_SECTIONS"; payload: string[] }
  | {
      type: "UPDATE_ITEM";
      payload: { id: string; updates: Partial<KnowledgeItem> };
    }
  | { type: "DELETE_ITEM"; payload: string }
  | {
      type: "CACHE_FILTERED_RESULTS";
      payload: {
        key: string;
        orgItems: KnowledgeItem[];
        agentItems: KnowledgeItem[];
      };
    }
  | { type: "CLEAR_CACHE" };

interface KnowledgeContextType {
  // State
  items: KnowledgeItem[];
  agents: Agent[];
  isLoading: boolean;
  agentsLoading: boolean;
  error: Error | null;
  isInitialized: boolean;

  // Filter states
  orgFilter: FilterType;
  agentFilter: FilterType;
  openSections: string[];

  // Actions
  fetchKnowledge: (force?: boolean) => Promise<void>;
  refreshKnowledge: () => Promise<void>;
  updateItem: (id: string, updates: Partial<KnowledgeItem>) => void;
  deleteItem: (id: string) => void;

  // Filter actions
  setOrgFilter: (filter: FilterType) => void;
  setAgentFilter: (filter: FilterType) => void;
  setOpenSections: (sections: string[]) => void;
  toggleSection: (sectionId: string) => void;

  // Computed data
  getFilteredItems: () => {
    orgItems: KnowledgeItem[];
    agentItems: KnowledgeItem[];
    agentsWithItems: Agent[];
    agentItemsGrouped: Record<string, KnowledgeItem[]>;
  };
  getTotalItemsCount: () => number;
}

const KnowledgeContext = createContext<KnowledgeContextType | undefined>(
  undefined,
);

// Initial state
const initialState: KnowledgeState = {
  items: [],
  agents: [],
  isLoading: false,
  agentsLoading: false,
  error: null,
  isInitialized: false,
  lastFetchTime: null,

  // Filter states
  orgFilter: "all",
  agentFilter: "all",
  openSections: ["organization-knowledge", "agent-specific-knowledge"],

  // Cache
  filteredResultsCache: new Map(),
};

// Reducer
function knowledgeReducer(
  state: KnowledgeState,
  action: KnowledgeAction,
): KnowledgeState {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };

    case "SET_AGENTS_LOADING":
      return { ...state, agentsLoading: action.payload };

    case "SET_ITEMS":
      return {
        ...state,
        items: action.payload,
        filteredResultsCache: new Map(), // Clear cache when items change
      };

    case "SET_AGENTS":
      return {
        ...state,
        agents: action.payload,
        filteredResultsCache: new Map(), // Clear cache when agents change
      };

    case "SET_ERROR":
      return { ...state, error: action.payload };

    case "SET_INITIALIZED":
      return { ...state, isInitialized: action.payload };

    case "SET_LAST_FETCH_TIME":
      return { ...state, lastFetchTime: action.payload };

    case "SET_ORG_FILTER":
      return {
        ...state,
        orgFilter: action.payload,
        filteredResultsCache: new Map(), // Clear cache when filters change
      };

    case "SET_AGENT_FILTER":
      return {
        ...state,
        agentFilter: action.payload,
        filteredResultsCache: new Map(),
      };

    case "SET_OPEN_SECTIONS":
      return { ...state, openSections: action.payload };

    case "UPDATE_ITEM":
      return {
        ...state,
        items: state.items.map((item) =>
          item.id === action.payload.id
            ? { ...item, ...action.payload.updates }
            : item,
        ),
        filteredResultsCache: new Map(), // Clear cache when data changes
      };

    case "DELETE_ITEM":
      return {
        ...state,
        items: state.items.filter((item) => item.id !== action.payload),
        filteredResultsCache: new Map(), // Clear cache when data changes
      };

    case "CACHE_FILTERED_RESULTS":
      const filteredResultsCache = new Map(state.filteredResultsCache);
      filteredResultsCache.set(action.payload.key, {
        orgItems: action.payload.orgItems,
        agentItems: action.payload.agentItems,
      });
      return { ...state, filteredResultsCache };

    case "CLEAR_CACHE":
      return { ...state, filteredResultsCache: new Map() };

    default:
      return state;
  }
}

export function KnowledgeProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(knowledgeReducer, initialState);

  // Check if we should fetch data (TTL-based caching)
  const shouldFetchKnowledge = useCallback(() => {
    if (!state.isInitialized) return true;
    if (!state.lastFetchTime) return true;

    const timeSinceLastFetch = Date.now() - state.lastFetchTime;
    return timeSinceLastFetch > CACHE_TTL;
  }, [state.isInitialized, state.lastFetchTime]);

  // Use hooks to fetch data, but manage when to fetch with context logic
  const shouldFetch = shouldFetchKnowledge();

  // Only fetch data when needed (TTL expired or force refresh)
  const {
    items: hookItems,
    isLoading: hookItemsLoading,
    refetch: refetchItems,
  } = useKnowledgeItems();
  const { agents: hookAgents, isLoading: hookAgentsLoading } =
    useCombinedAgentList();

  // Sync hook data with context when it loads and when we need it
  useEffect(() => {
    if (!hookItemsLoading && !hookAgentsLoading && hookItems && hookAgents) {
      // Only update if we don't have cached data or it's stale
      if (shouldFetch || !state.isInitialized) {
        console.log("🔄 Syncing fresh data to context");
        dispatch({ type: "SET_ITEMS", payload: hookItems });
        dispatch({ type: "SET_AGENTS", payload: hookAgents });
        dispatch({ type: "SET_LAST_FETCH_TIME", payload: Date.now() });
        dispatch({ type: "SET_INITIALIZED", payload: true });
      }
    }
  }, [
    hookItems,
    hookAgents,
    hookItemsLoading,
    hookAgentsLoading,
    shouldFetch,
    state.isInitialized,
  ]);

  // Update loading states from hooks
  useEffect(() => {
    dispatch({ type: "SET_LOADING", payload: hookItemsLoading });
  }, [hookItemsLoading]);

  useEffect(() => {
    dispatch({ type: "SET_AGENTS_LOADING", payload: hookAgentsLoading });
  }, [hookAgentsLoading]);

  // Fetch knowledge items and agents
  const fetchKnowledge = useCallback(
    async (force: boolean = false) => {
      if (!force && !shouldFetchKnowledge()) {
        console.log("📦 Using cached knowledge data");
        return;
      }

      console.log("🔄 Force fetching knowledge data");
      // Trigger refetch from hooks
      refetchItems();
    },
    [shouldFetchKnowledge, refetchItems],
  );

  // Refresh knowledge (force fetch)
  const refreshKnowledge = useCallback(async () => {
    console.log("🔄 Refreshing knowledge data");
    await fetchKnowledge(true);
  }, [fetchKnowledge]);

  // Update a specific item
  const updateItem = useCallback(
    (id: string, updates: Partial<KnowledgeItem>) => {
      dispatch({
        type: "UPDATE_ITEM",
        payload: { id, updates },
      });
    },
    [],
  );

  // Delete an item
  const deleteItem = useCallback((id: string) => {
    dispatch({ type: "DELETE_ITEM", payload: id });
  }, []);

  // Filter action creators
  const setOrgFilter = useCallback((filter: FilterType) => {
    dispatch({ type: "SET_ORG_FILTER", payload: filter });
  }, []);

  const setAgentFilter = useCallback((filter: FilterType) => {
    dispatch({ type: "SET_AGENT_FILTER", payload: filter });
  }, []);

  const setOpenSections = useCallback((sections: string[]) => {
    dispatch({ type: "SET_OPEN_SECTIONS", payload: sections });
  }, []);

  const toggleSection = useCallback(
    (sectionId: string) => {
      const newSections = state.openSections.includes(sectionId)
        ? state.openSections.filter((id) => id !== sectionId)
        : [...state.openSections, sectionId];
      setOpenSections(newSections);
    },
    [state.openSections, setOpenSections],
  );

  // Get filtered items with caching
  const getFilteredItems = useCallback(() => {
    const cacheKey = `${state.orgFilter}-${state.agentFilter}`;

    // Check cache first
    const cached = state.filteredResultsCache.get(cacheKey);
    if (cached) {
      console.log(`📦 Using cached filtered knowledge results for ${cacheKey}`);

      // Get agents with items
      const agentItemsGrouped = cached.agentItems.reduce(
        (acc, item) => {
          const agentId = item.agent_id || item.voice_agent_id;
          if (agentId) {
            if (!acc[agentId]) acc[agentId] = [];
            acc[agentId].push(item);
          }
          return acc;
        },
        {} as Record<string, KnowledgeItem[]>,
      );

      const agentsWithItems = state.agents.filter(
        (agent) => agentItemsGrouped[agent.id],
      );

      return {
        orgItems: cached.orgItems,
        agentItems: cached.agentItems,
        agentsWithItems,
        agentItemsGrouped,
      };
    }

    console.log("🔄 Computing filtered knowledge items");

    // Split items into organization-level and agent-specific
    let orgItems = state.items.filter(
      (item) => !item.agent_id && !item.voice_agent_id,
    );
    let agentItems = state.items.filter(
      (item) => item.agent_id || item.voice_agent_id,
    );

    // Apply organization filter
    if (state.orgFilter !== "all") {
      orgItems = orgItems.filter((item) => item.type === state.orgFilter);
    }

    // Apply agent filter
    if (state.agentFilter !== "all") {
      agentItems = agentItems.filter((item) => item.type === state.agentFilter);
    }

    // Group agent items by agent
    const agentItemsGrouped = agentItems.reduce(
      (acc, item) => {
        const agentId = item.agent_id || item.voice_agent_id;
        if (agentId) {
          if (!acc[agentId]) acc[agentId] = [];
          acc[agentId].push(item);
        }
        return acc;
      },
      {} as Record<string, KnowledgeItem[]>,
    );

    // Get agents with items
    const agentsWithItems = state.agents.filter(
      (agent) => agentItemsGrouped[agent.id],
    );

    // Cache the results
    dispatch({
      type: "CACHE_FILTERED_RESULTS",
      payload: { key: cacheKey, orgItems, agentItems },
    });

    return { orgItems, agentItems, agentsWithItems, agentItemsGrouped };
  }, [
    state.items,
    state.agents,
    state.orgFilter,
    state.agentFilter,
    state.filteredResultsCache,
  ]);

  // Get total items count
  const getTotalItemsCount = useCallback(() => {
    return state.items.length;
  }, [state.items.length]);

  // Auto-fetch on mount
  useEffect(() => {
    fetchKnowledge();
  }, [fetchKnowledge]);

  const contextValue: KnowledgeContextType = {
    // State
    items: state.items,
    agents: state.agents,
    isLoading: state.isLoading,
    agentsLoading: state.agentsLoading,
    error: state.error,
    isInitialized: state.isInitialized,

    // Filter states
    orgFilter: state.orgFilter,
    agentFilter: state.agentFilter,
    openSections: state.openSections,

    // Actions
    fetchKnowledge,
    refreshKnowledge,
    updateItem,
    deleteItem,

    // Filter actions
    setOrgFilter,
    setAgentFilter,
    setOpenSections,
    toggleSection,

    // Computed data
    getFilteredItems,
    getTotalItemsCount,
  };

  return (
    <KnowledgeContext.Provider value={contextValue}>
      {children}
    </KnowledgeContext.Provider>
  );
}

export function useKnowledge() {
  const context = useContext(KnowledgeContext);
  if (context === undefined) {
    throw new Error("useKnowledge must be used within a KnowledgeProvider");
  }
  return context;
}
